import { useMutation } from '@tanstack/react-query';

import {
  deleteV1IdentityCurrentInviteToJoinById,
  deleteV1IdentityCurrentRequestToJoinById,
  postV1IdentityCurrentInviteToJoinByIdAccept,
  postV1RequestToJoin,
  type RequestToJoinPostBody,
} from '../../api/generated/pap';
import { deleteV1IdentityCurrentRequestBusinessPartnerById } from '../../api/generated/pm';
import {
  invalidateCurrentBusinessPartnerDivisionRequests,
  invalidateCurrentBusinessPartnerDivisions,
} from '../Divisions/divisions.queries';
import { invalidateCurrentIdentityBusinessPartners } from '../Settings/businessPartner.queries';

/**
 * Mutation hooks for what the current Identity does with its own Requests to
 * Join, Invites to Join and Business Partner Requests. Requesting or
 * withdrawing membership changes which Divisions the identity can see, so
 * those mutations clear the Divisions reads. See
 * `../Settings/IdentityProviders/identityProviders.mutations.ts` for the
 * pattern.
 */

/** Ask to join a Business Partner (an Organization or one of its Divisions). */
export const useCreateRequestToJoin = () =>
  useMutation({
    mutationFn: async (payload: RequestToJoinPostBody) => {
      const { data } = await postV1RequestToJoin({ body: payload, throwOnError: true });
      return data;
    },
    onSuccess: () => {
      invalidateCurrentBusinessPartnerDivisions();
      invalidateCurrentBusinessPartnerDivisionRequests();
    },
  });

/** Withdraw one of the current Identity's own Requests to Join. */
export const useDeleteRequestToJoin = () =>
  useMutation({
    mutationFn: async (id: string) => {
      await deleteV1IdentityCurrentRequestToJoinById({ path: { id }, throwOnError: true });
    },
    onSuccess: () => {
      invalidateCurrentBusinessPartnerDivisions();
      invalidateCurrentBusinessPartnerDivisionRequests();
    },
  });

export const useAcceptInviteToJoin = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await postV1IdentityCurrentInviteToJoinByIdAccept({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      invalidateCurrentIdentityBusinessPartners();
    },
  });

export const useRejectInviteToJoin = () =>
  useMutation({
    mutationFn: async (id: string) => {
      await deleteV1IdentityCurrentInviteToJoinById({ path: { id }, throwOnError: true });
    },
  });

/** Cancel one of the current Identity's pending Business Partner Requests. */
export const useCancelRequestBusinessPartner = () =>
  useMutation({
    mutationFn: async (id: string) => {
      await deleteV1IdentityCurrentRequestBusinessPartnerById({ path: { id }, throwOnError: true });
    },
    onSuccess: () => {
      invalidateCurrentBusinessPartnerDivisions();
      invalidateCurrentBusinessPartnerDivisionRequests();
    },
  });
